import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, Trash2, Tag, ShoppingBag, ShieldCheck, Repeat, Sparkles, Coins, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

const Cart = () => {
  const nav = useNavigate();
  const {
    cart, updateQty, removeFromCart,
    coupon, applyCoupon, clearCoupon, computeCouponDiscount, availableCoupons,
    coins, coinsRedeem, setCoinsRedeem,
  } = useApp();
  const [couponInput, setCouponInput] = useState('');
  const [couponMsg, setCouponMsg] = useState(null); // {ok, msg}
  const [showAllCoupons, setShowAllCoupons] = useState(false);

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const couponDiscount = computeCouponDiscount(subtotal);
  const afterCoupon = Math.max(0, subtotal - couponDiscount);
  // Loyalty: 1 coin = ₹1, max = 20% of subtotal (rounded) OR user coin balance
  const maxRedeem = Math.min(coins, Math.floor(subtotal * 0.2));
  const loyaltyApplied = Math.min(coinsRedeem, maxRedeem, afterCoupon);
  const total = Math.max(0, afterCoupon - loyaltyApplied);

  const onApplyCoupon = () => {
    const r = applyCoupon(couponInput);
    setCouponMsg(r);
    if (r.ok) setCouponInput('');
  };
  const onPickCoupon = (code) => {
    const r = applyCoupon(code);
    setCouponMsg(r);
    setShowAllCoupons(false);
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-32 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-extrabold">Your Cart</h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <h1 className="hidden md:block text-2xl font-extrabold mb-5">Your Cart</h1>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center">
              <ShoppingBag className="w-9 h-9 text-neutral-400" />
            </div>
            <p className="mt-4 text-neutral-800 font-semibold">Your cart is empty</p>
            <p className="text-sm text-neutral-500 mt-1">Browse services and add your first booking</p>
            <button onClick={() => nav('/home')} className="mt-5 bg-[#2E7D32] text-white px-5 py-2.5 rounded-lg font-semibold">Explore services</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-3">
              {cart.map((item) => {
                const isSub = item.type === 'subscription';
                return (
                <div key={item.id} className="bg-white border border-neutral-200 rounded-xl p-3 flex gap-3">
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1">
                    {isSub && (
                      <span className="inline-flex items-center gap-1 bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider mb-1">
                        <Repeat className="w-3 h-3" /> Subscription
                      </span>
                    )}
                    <h3 className="font-semibold text-sm leading-tight">{item.name}</h3>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {isSub ? `Billed ₹${item.price} per ${item.period}` : `₹${item.price} per unit`}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      {isSub ? (
                        <span className="text-[11px] text-neutral-500">1 plan (fixed)</span>
                      ) : (
                        <div className="flex items-center border border-neutral-200 rounded-lg overflow-hidden">
                          <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 flex items-center justify-center text-[#EA580C] hover:bg-neutral-50"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="px-2 text-sm font-semibold">{item.qty}</span>
                          <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 flex items-center justify-center text-[#EA580C] hover:bg-neutral-50"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                      <button onClick={() => removeFromCart(item.id)} className="ml-auto text-neutral-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#EA580C]">₹{item.price * item.qty}</p>
                    {isSub && <p className="text-[10px] text-neutral-500">/{item.period}</p>}
                  </div>
                </div>
                );
              })}

              {/* Coupon */}
              <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="cart-coupon-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                    <Tag className="w-4 h-4 text-[#2E7D32]" />
                  </div>
                  <p className="text-sm font-bold flex-1">Apply Coupon</p>
                  {!coupon && (
                    <button data-testid="cart-view-all-coupons" onClick={() => setShowAllCoupons((v) => !v)} className="text-xs font-semibold text-[#2E7D32] hover:underline">
                      {showAllCoupons ? 'Hide' : 'View all'}
                    </button>
                  )}
                </div>

                {coupon ? (
                  <div className="flex items-center gap-2 bg-[#E8F5E9] border border-[#C8E6C9] rounded-lg px-3 py-2" data-testid="cart-coupon-applied">
                    <Sparkles className="w-4 h-4 text-[#2E7D32]" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#1B5E20]">{coupon.code}</p>
                      <p className="text-[11px] text-neutral-600">{coupon.label}</p>
                    </div>
                    <button data-testid="cart-coupon-remove" onClick={() => { clearCoupon(); setCouponMsg(null); }} className="text-neutral-500 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input
                        data-testid="cart-coupon-input"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="Enter code (e.g. FLAT100)"
                        className="flex-1 px-3 py-2.5 rounded-lg border border-neutral-200 text-sm uppercase tracking-wider focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20" />
                      <button
                        data-testid="cart-coupon-apply-btn"
                        onClick={onApplyCoupon}
                        disabled={!couponInput}
                        className="px-4 py-2.5 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors">
                        Apply
                      </button>
                    </div>
                    {couponMsg && (
                      <p className={`mt-2 text-xs ${couponMsg.ok ? 'text-[#2E7D32]' : 'text-red-600'}`}>{couponMsg.msg}</p>
                    )}
                    {showAllCoupons && (
                      <div className="mt-3 space-y-2 border-t border-neutral-100 pt-3" data-testid="cart-all-coupons-list">
                        {availableCoupons.map((c) => (
                          <div key={c.code} className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-neutral-200 hover:border-[#2E7D32] hover:bg-[#F0F8F0] transition-colors">
                            <div className="w-8 h-8 rounded bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                              <Tag className="w-3.5 h-3.5 text-[#2E7D32]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-neutral-900">{c.code}</p>
                              <p className="text-[11px] text-neutral-500">{c.label}</p>
                            </div>
                            <button
                              data-testid={`cart-apply-coupon-${c.code}`}
                              onClick={() => onPickCoupon(c.code)}
                              className="text-xs font-semibold text-[#2E7D32] px-2 py-1 rounded hover:bg-[#E8F5E9]">
                              Apply
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Loyalty */}
              {coins > 0 && subtotal > 0 && (
                <div className="bg-gradient-to-r from-[#FFFBEB] to-white border border-[#FEF3C7] rounded-xl p-4" data-testid="cart-loyalty-section">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                      <Coins className="w-4 h-4 text-[#D97706]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold">Clean24 Coins</p>
                      <p className="text-[11px] text-neutral-500">Balance: <span className="font-bold text-[#D97706]">{coins}</span> coins · 1 coin = ₹1</p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="cart-loyalty-toggle"
                        checked={coinsRedeem > 0}
                        onChange={(e) => setCoinsRedeem(e.target.checked ? maxRedeem : 0)}
                        disabled={maxRedeem === 0}
                        className="w-4 h-4 accent-[#D97706]"
                      />
                      <span className="text-xs font-semibold text-neutral-700">Redeem</span>
                    </label>
                  </div>

                  {maxRedeem > 0 ? (
                    <>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max={maxRedeem}
                          step="1"
                          value={Math.min(coinsRedeem, maxRedeem)}
                          onChange={(e) => setCoinsRedeem(Number(e.target.value))}
                          data-testid="cart-loyalty-slider"
                          className="flex-1 accent-[#D97706]"
                        />
                        <span className="text-sm font-bold min-w-[64px] text-right text-[#D97706]">
                          {Math.min(coinsRedeem, maxRedeem)} <span className="text-[10px] font-normal text-neutral-500">coins</span>
                        </span>
                      </div>
                      <p className="text-[11px] text-neutral-500 mt-1">You can redeem up to {maxRedeem} coins on this order (20% cap)</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-neutral-500">Add more items to unlock coin redemption (min order to redeem)</p>
                  )}
                </div>
              )}
            </div>

            <div className="md:sticky md:top-24 md:self-start">
              <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="cart-bill-details">
                <p className="text-sm font-bold mb-3">Bill Details</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-neutral-600">Item total</span><span>₹{subtotal}</span></div>
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-[#2E7D32]" data-testid="cart-bill-coupon">
                      <span>Coupon ({coupon?.code})</span><span>- ₹{couponDiscount}</span>
                    </div>
                  )}
                  {loyaltyApplied > 0 && (
                    <div className="flex justify-between text-[#D97706]" data-testid="cart-bill-loyalty">
                      <span>Coins redeemed ({loyaltyApplied})</span><span>- ₹{loyaltyApplied}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-neutral-600">Visitation charge</span><span>₹0</span></div>
                  <div className="border-t border-dashed border-neutral-200 my-2" />
                  <div className="flex justify-between font-bold text-base" data-testid="cart-bill-total"><span>Total</span><span>₹{total}</span></div>
                </div>
                <button data-testid="cart-proceed-btn" onClick={() => nav('/checkout')} className="mt-4 hidden md:block w-full bg-[#2E7D32] hover:bg-[#276D2A] text-white rounded-lg py-3 font-semibold transition-colors">
                  Proceed to book • ₹{total}
                </button>
                <div className="mt-3 pt-3 border-t border-neutral-100 flex items-start gap-2 text-[11px] text-neutral-500">
                  <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#2E7D32]" />
                  <p>100% guarantee. If you're not happy, we'll re-clean free or refund.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {cart.length > 0 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-100 px-4 py-3 z-40">
          <button onClick={() => nav('/checkout')} className="w-full bg-[#2E7D32] hover:bg-[#276D2A] text-white rounded-xl py-3.5 font-semibold">
            Proceed to book • ₹{total}
          </button>
        </div>
      )}
      <BottomNav />
    </div>
  );
};

export default Cart;
